import { useMemo, useState } from 'react';
import type React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BellPlus, Mail, Search, ShieldOff } from 'lucide-react';
import { Button, Chip } from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { CampaignStatusBadge } from '../components/CampaignStatusBadge';
import { useNotificationCampaigns } from '../hooks/useNotificationCampaigns';
import type { CampaignDto, CampaignStatus } from '../types';

const STATUS_OPTIONS: CampaignStatus[] = [
  'DRAFT',
  'SENDING',
  'SENT',
  'PARTIALLY_FAILED',
  'FAILED',
  'CANCELLED',
  'RETRACTED',
];

const formatDate = (value?: string | null) => (value ? new Date(value).toLocaleString() : '-');
const readPercent = (campaign: CampaignDto) =>
  campaign.inAppCreated > 0
    ? `${Math.round((campaign.readCount / campaign.inAppCreated) * 100)}%`
    : '-';

const audienceText = (campaign: CampaignDto) => {
  if (campaign.audienceType === 'USERS') return `USERS · ${campaign.audienceUsers.length}`;
  if (campaign.audienceType === 'ALL') return `ALL · ${campaign.totalRecipients || 'send time'}`;
  return `SEGMENT · ${campaign.totalRecipients || 'preview'}`;
};

const emailStats = (campaign: CampaignDto) => {
  if (!campaign.email) return '-';
  return `${campaign.email.sent + campaign.email.delivered} / ${
    campaign.email.failed + campaign.email.bounced
  }`;
};

export const NotificationCampaignListPage: React.FC = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<CampaignStatus | ''>('');
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const { data, isLoading, isFetching } = useNotificationCampaigns({
    status,
    search: search.trim() || undefined,
    page: pagination.page + 1,
    limit: pagination.pageSize,
  });

  const columns = useMemo<GridColDef<CampaignDto>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 240,
        renderCell: (params) => (
          <div className="min-w-0">
            <div className="truncate font-semibold text-gray-900 dark:text-white">
              {params.row.name}
            </div>
            <div className="truncate text-xs text-gray-500 dark:text-slate-400">
              {params.row.publicId}
            </div>
          </div>
        ),
      },
      {
        field: 'audienceType',
        headerName: 'Audience',
        width: 170,
        valueGetter: (_, row) => audienceText(row),
      },
      {
        field: 'channels',
        headerName: 'Channels',
        width: 170,
        renderCell: (params) => (
          <div className="flex flex-wrap gap-1">
            {params.row.channels.includes('IN_APP') && (
              <Chip icon={<Bell size={14} />} label="In-app" size="small" />
            )}
            {params.row.channels.includes('EMAIL') && (
              <Chip icon={<Mail size={14} />} label="Email" size="small" />
            )}
          </div>
        ),
      },
      {
        field: 'category',
        headerName: 'Category',
        width: 160,
        renderCell: (params) => (
          <Chip label={String(params.value).replace('_', ' ')} size="small" />
        ),
      },
      {
        field: 'severity',
        headerName: 'Severity',
        width: 120,
        renderCell: (params) => <Chip label={params.value} size="small" />,
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 140,
        renderCell: (params) => <CampaignStatusBadge status={params.row.status} />,
      },
      { field: 'totalRecipients', headerName: 'Recipients', width: 120 },
      {
        field: 'emailStats',
        headerName: 'Email sent/failed',
        width: 150,
        valueGetter: (_, row) => emailStats(row),
      },
      {
        field: 'readRate',
        headerName: 'Read %',
        width: 110,
        valueGetter: (_, row) => readPercent(row),
      },
      {
        field: 'startedAt',
        headerName: 'Started',
        width: 180,
        valueFormatter: (value) => formatDate(value as string | null),
      },
      {
        field: 'finishedAt',
        headerName: 'Finished',
        width: 180,
        valueFormatter: (value) => formatDate(value as string | null),
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        width: 220,
        valueGetter: (_, row) => row.createdBy?.email || '-',
      },
    ],
    []
  );

  return (
    <div className="min-h-screen min-w-0 bg-slate-50 p-4 dark:bg-slate-900 md:p-6">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Notifications</h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-slate-400">
            Compose, send, and track in-app notification campaigns.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outlined"
            startIcon={<ShieldOff size={18} />}
            onClick={() => navigate('/admin-mail/suppressions')}
          >
            Suppression list
          </Button>
          <Button
            variant="contained"
            startIcon={<BellPlus size={18} />}
            onClick={() => navigate('/admin-mail/new')}
          >
            New notification
          </Button>
        </div>
      </div>

      <div className="mb-4 grid gap-3 md:grid-cols-[1fr_220px]">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((current) => ({ ...current, page: 0 }));
            }}
            placeholder="Search campaign name..."
            className="h-11 w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-white"
          />
        </div>
        <select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as CampaignStatus | '');
            setPagination((current) => ({ ...current, page: 0 }));
          }}
          className="h-11 rounded-lg border border-gray-300 bg-white px-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3 text-sm text-gray-500 dark:text-slate-400">
        {isFetching && !isLoading ? 'Refreshing campaigns...' : `${data?.total ?? 0} campaigns`}
      </div>

      <div className="rounded-lg bg-white shadow-sm dark:bg-slate-800" style={{ height: 650 }}>
        <DataGrid
          rows={data?.items || []}
          getRowId={(row) => row.publicId}
          columns={columns}
          rowCount={data?.total || 0}
          loading={isLoading}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 20, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) =>
            navigate(
              params.row.status === 'DRAFT'
                ? `/admin-mail/${params.row.publicId}/edit`
                : `/admin-mail/${params.row.publicId}`
            )
          }
          sx={{ border: 'none', cursor: 'pointer' }}
        />
      </div>
    </div>
  );
};
